<template>
  <AppLayout>
    <template #header>
      <PageHeadingWithActions
        :title="plane.name + ' (' + plane.tail_number + ')'"
      >
        <template #actions>
          <DropdownResponsive>
            <template #trigger>
              <DropdownButton />
            </template>

            <DropdownItemGroup v-if="props.canManage">
              <DropdownItem
                :href="route('planes.users.index', props.plane)"
                label="People & Roles"
                :icon="UsersIcon"
              />

              <DropdownItem
                :href="route('planes.edit', props.plane)"
                label="Edit Plane"
                :icon="PencilAltIcon"
              />
            </DropdownItemGroup>

            <DropdownItemGroup v-if="props.canManagePilots">
              <DropdownItem
                :href="route('planes.pilots.index', props.plane)"
                label="Pilots"
                :icon="UserGroupIcon"
              />
            </DropdownItemGroup>

            <DropdownItemGroup>
              <DropdownItem
                @click="showingSyncToCalendarModal = true"
                label="Sync to Calendar"
                :icon="CalendarIcon"
              />

              <DropdownItem
                :href="
                  'https://flightaware.com/live/flight/' +
                  props.plane.tail_number
                "
                external
                label="View on FlightAware"
                :icon="ExternalLinkIcon"
              />
            </DropdownItemGroup>
          </DropdownResponsive>

          <DropdownBase v-if="useUser.authorizations.trips.create">
            <template #trigger>
              <DropdownButtonWithButton
                class="ml-2"
                :href="
                  route('trips.create', { plane: props.plane.tail_number })
                "
                color="default"
                label="New Trip"
              />
            </template>

            <DropdownItemGroup>
              <DropdownItem
                @click="showingHoldFormModal = true"
                label="Create Hold"
                :icon="BanIcon"
              />
            </DropdownItemGroup>
          </DropdownBase>
        </template>
      </PageHeadingWithActions>
    </template>
    <div class="space-y-6">
      <AlertWithLinkOnRight
        v-if="props.holds > 0"
        label="View holds"
        :href="route('planes.holds.index', props.plane)"
        >There {{ singularOrPlural(props.holds, 'is', 'are') }}
        {{ props.holds }} upcoming
        {{ singularOrPlural(props.holds, 'hold', 'holds') }} for this
        plane.</AlertWithLinkOnRight
      >

      <StackedListContainer v-if="props.upcoming.length">
        <template #before>
          <CardHeadingSimple title="Upcoming" />
        </template>

        <StackedListRow
          v-for="trip in props.upcoming"
          :key="trip.id"
          :href="route('trips.show', trip)"
        >
          <TripStackedListRow :trip="trip" />
        </StackedListRow>
      </StackedListContainer>

      <StackedListContainer v-if="props.past.data.length">
        <template #before>
          <CardHeadingSimple title="Past Trips" />
        </template>

        <StackedListRow
          v-for="trip in props.past.data"
          :key="trip.id"
          :href="route('trips.show', trip)"
        >
          <TripStackedListRow :trip="trip" />
        </StackedListRow>
        <CardPagination :meta="props.past.meta" />
      </StackedListContainer>
    </div>

    <SyncToCalendarModal
      :plane="props.plane"
      :show="showingSyncToCalendarModal"
      @close="showingSyncToCalendarModal = false"
    />

    <HoldFormModal
      :plane="props.plane"
      :show="showingHoldFormModal"
      @close="showingHoldFormModal = false"
    />
  </AppLayout>
</template>

<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import StackedListContainer from '@/Common/Lists/StackedLists/StackedListContainer.vue'
import CardPagination from '@/Common/Navigation/Pagination/CardPagination.vue'
import StackedListRow from '@/Common/Lists/StackedLists/StackedListRow.vue'
import TripStackedListRow from '../Trips/TripStackedListRow.vue'
import PageHeadingWithActions from '@/Common/Headings/PageHeadings/PageHeadingWithActions.vue'
import DropdownResponsive from '@/Common/Elements/Dropdowns/DropdownResponsive.vue'
import DropdownButton from '@/Common/Elements/Dropdowns/DropdownButton.vue'
import DropdownItem from '@/Common/Elements/Dropdowns/DropdownItem.vue'
import DropdownItemGroup from '@/Common/Elements/Dropdowns/DropdownItemGroup.vue'
import {
  ExternalLinkIcon,
  CalendarIcon,
  BanIcon,
  UsersIcon,
  UserGroupIcon,
  PencilAltIcon,
} from '@heroicons/vue/solid'
import { ref } from 'vue'
import SyncToCalendarModal from './SyncToCalendarModal.vue'
import CardHeadingSimple from '@/Common/Headings/CardHeadings/CardHeadingSimple.vue'
import DropdownButtonWithButton from '@/Common/Elements/Dropdowns/DropdownButtonWithButton.vue'
import DropdownBase from '@/Common/Elements/Dropdowns/DropdownBase.vue'
import HoldFormModal from './Holds/HoldFormModal.vue'
import AlertWithLinkOnRight from '@/Common/Feedback/Alerts/AlertWithLinkOnRight.vue'
import { singularOrPlural } from '@/Util/string'
import { useUser } from '@/Util/UseUser'

const props = defineProps({
  plane: Object,
  past: Object,
  upcoming: Array,
  holds: Number,
  canManage: Boolean,
  canManagePilots: Boolean,
})

const showingSyncToCalendarModal = ref(false)

// Holds
const showingHoldFormModal = ref(false)
</script>
